import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  usePagination,
} from '../../components/ui/pagination';
import AttemptPatternPieChartCard from '../Components/AttemptPatternPieChartCard';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import AllQs from '../../../public/Icons/AllSubjects.svg';
import QuestionDrawer from '../../components/drawers/QuestionDrawer';
import { formatTestTitle } from '../../utils/formatTestTitle';
import { ContentAreaSkeleton, FiltersSkeleton } from '../Components/MistakesLoaders';
import QuestionListItem from '../Components/QuestionListItem'; // Import the new component
import { AppLayout } from '../../Layout/AppLayout';
import { usePackStorage } from '../../Layout/usePackStorage';

const transformDetailedDataForPieChart = (tags, totalIncorrect, totalUnattempted) => {
  if (!tags || tags.length === 0) return [];

  return tags.map((tag) => {
    const incorrectCount = tag.incorrectCount || 0;
    const unattemptedCount = tag.unattemptedCount || 0;
    const incorrectPercentage = totalIncorrect > 0 ? (incorrectCount / totalIncorrect) * 100 : 0;
    const unattemptedPercentage = totalUnattempted > 0 ? (unattemptedCount / totalUnattempted) * 100 : 0;

    return {
      tagTitle: tag.title,
      icon: tag.icon,
      incorrectPercentage: incorrectPercentage.toFixed(2),
      unattemptedPercentage: unattemptedPercentage.toFixed(2),
    };
  });
};

export default function TestWiseMistakesDetailed() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { testTitle } = location.state || { testTitle: 'Test' };
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();

  const [attemptingPatternExpanded, setAttemptingPatternExpanded] = useState(false);
  const [detailedQuestionFilter, setDetailedQuestionFilter] = useState('All');
  const [communitySolutionFilter, setCommunitySolutionFilter] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState({ _id: 'all-qs', title: 'All Qs', color: '#868E96' });
  const [detailedQuestions, setDetailedQuestions] = useState([]);
  const [subjectsForDetailedView, setSubjectsForDetailedView] = useState([]);
  const [detailedTotalPages, setDetailedTotalPages] = useState(1);
  const [detailedCurrentPage, setDetailedCurrentPage] = useState(1);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [discoveredTags, setDiscoveredTags] = useState(new Map());
  const [selectedMistakeReason, setSelectedMistakeReason] = useState('All');
  const [chapterOptions, setChapterOptions] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState('All');
  const [attemptOverviewPieData, setAttemptOverviewPieData] = useState([]);

  const [isChapterSelectOpen, setIsChapterSelectOpen] = useState(false);
  const [isMistakeReasonSelectOpen, setIsMistakeReasonSelectOpen] = useState(false);
  const [isQuestionDrawerOpen, setIsQuestionDrawerOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const paginationRange = usePagination({ totalPages: detailedTotalPages, currentPage: detailedCurrentPage });
  const selectedPackId = localStorage.getItem('selectedPackId');

  const mistakeReasonsOptions = useMemo(() => {
    const options = Array.from(discoveredTags, ([id, title]) => ({ id, title }));
    return [{ id: 'All', title: 'All' }, ...options];
  }, [discoveredTags]);

  useEffect(() => {
    if (selectedPackId) {
      axios
        .get(`/api/packs/${selectedPackId}/subjects`)
        .then((response) => {
          if (response.data.success && Array.isArray(response.data.data)) {
            setSubjectsForDetailedView(response.data.data);
          }
        })
        .catch((error) => console.error('Error fetching subjects:', error));
    }
  }, [selectedPackId]);

  useEffect(() => {
    if (!testId) return;
    setIsLoadingContent(true);
    const params = {
      offset: (detailedCurrentPage - 1) * 10,
      limit: 10,
      ...(detailedQuestionFilter === 'Attempted Incorrect' && { incorrect: 'true' }),
      ...(detailedQuestionFilter === 'Not Attempted' && { unattempted: 'true' }),
      ...(detailedQuestionFilter === 'Important' && { important: 'true' }),
      ...(communitySolutionFilter && { communitySolution: 'true' }),
      ...(selectedSubject._id && selectedSubject._id !== 'all-qs' && { subject: selectedSubject._id }),
      ...(selectedMistakeReason !== 'All' && { mistakeReason: selectedMistakeReason }),
      ...(selectedChapter !== 'All' && { chapter: selectedChapter }),
    };

    axios
      .get(`/api/v2/answers/${testId}/test-wise-detailed`, { params })
      .then((response) => {
        if (response.data.success) {
          setDetailedQuestions(Array.isArray(response.data.data) ? response.data.data : []);
          setDetailedTotalPages(response.data.totalPages || 1);
          setDetailedCurrentPage(response.data.currentPage || 1);
        } else {
          setDetailedQuestions([]);
          setDetailedTotalPages(1);
        }
      })
      .catch((error) => {
        console.error(`Error fetching test-wise detailed questions for ${testId}:`, error);
      })
      .finally(() => {
        setIsLoadingContent(false);
      });
  }, [
    testId,
    detailedQuestionFilter,
    communitySolutionFilter,
    selectedSubject,
    detailedCurrentPage,
    selectedMistakeReason,
    selectedChapter,
  ]);

  useEffect(() => {
    if (!testId) return;
    setIsLoadingFilters(true);
    axios
      .get(`/api/v2/answers/${testId}/test-wise-detailed`, { params: { limit: 1 } })
      .then((response) => {
        if (response.data.success) {
          const { userQuestionTags, totalIncorrect, totalUnattempted, chapters } = response.data;
          const pieChartData = transformDetailedDataForPieChart(userQuestionTags, totalIncorrect, totalUnattempted);
          setAttemptOverviewPieData(pieChartData);
          if (userQuestionTags) {
            const mistakeTags = userQuestionTags
              .filter((tag) => tag.title !== 'Not Tagged')
              .map((tag) => ({ id: tag._id, title: tag.title }));
            setDiscoveredTags(new Map(mistakeTags.map((tag) => [tag.id, tag.title])));
          }
          if (chapters) {
            setChapterOptions([{ _id: 'All', title: 'All Chapters' }, ...chapters]);
          }
        }
      })
      .catch((error) => console.error(`Error fetching initial data for test ${testId}:`, error))
      .finally(() => setIsLoadingFilters(false));
  }, [testId]);

  const handleSubjectChange = (subject) => {
    setSelectedSubject({ ...subject, color: subject.color || '#868E96' });
    setDetailedCurrentPage(1);
  };
  const handleMistakeReasonChange = (reasonId) => {
    setSelectedMistakeReason(reasonId);
    setDetailedCurrentPage(1);
  };
  const handleChapterChange = (chapterId) => {
    setSelectedChapter(chapterId);
    setDetailedCurrentPage(1);
  };
  const handleQuestionClick = (questionIndex) => {
    setCurrentQuestionIndex(questionIndex);
    setIsQuestionDrawerOpen(true);
  };
  const handleQuestionChange = (newIndex) => setCurrentQuestionIndex(newIndex);
  const allTabs = [{ _id: 'all-qs', title: 'All Qs', icon: null, color: '#868E96' }, ...subjectsForDetailedView];
  const formattedTestTitle = formatTestTitle(testTitle);

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="flex-1 min-w-0 overflow-hidden flex justify-center">
        <div className="h-full w-full max-w-7xl">
          <div className="w-full px-4 py-6 md:py-8">
            <div className="flex flex-col items-start gap-2 mb-6">
              <Button
                onClick={() => navigate(`/v3/my-mistakes?type=test-wise`)}
                size="icon"
                className="text-[var(--q3-neutral-light)] mb-2 bg-[var(--q3-surface-dimmer)] hover:bg-[var(--q3-surface-dimmer)]/90 rounded-full"
              >
                <ArrowLeft size={24} />
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-[var(--q3-neutral-default)]">{testTitle}</h1>
              </div>
            </div>

            {isLoadingFilters ? (
              <FiltersSkeleton />
            ) : (
              <div className="flex items-center flex-wrap gap-2 mb-6">
                <Select
                  value={selectedChapter}
                  onValueChange={handleChapterChange}
                  onOpenChange={setIsChapterSelectOpen}
                >
                  <SelectTrigger
                    disabled={chapterOptions.length === 0}
                    className={`cursor-pointer w-auto rounded-full`}
                  >
                    <SelectValue placeholder="Select Chapter" />
                  </SelectTrigger>
                  <SelectContent>
                    {chapterOptions.map((chapter) => (
                      <SelectItem key={chapter._id} value={chapter._id}>
                        {chapter.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="h-8 w-[1px] bg-[var(--q3-stroke-normal)]" />

                <Select
                  value={selectedMistakeReason}
                  onValueChange={handleMistakeReasonChange}
                  onOpenChange={setIsMistakeReasonSelectOpen}
                >
                  <SelectTrigger
                    disabled={mistakeReasonsOptions.length === 0}
                    className={`cursor-pointer w-auto rounded-full`}
                  >
                    Mistake Reason : <SelectValue placeholder="Mistake Reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {mistakeReasonsOptions.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="h-8 w-[1px] bg-[var(--q3-stroke-normal)]" />
                <Button
                  variant={detailedQuestionFilter === 'All' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDetailedQuestionFilter('All')}
                  className={
                    detailedQuestionFilter === 'All'
                      ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]/90 text-[var(--q3-neutral-default_inverted)] rounded-full'
                      : 'rounded-full'
                  }
                >
                  All {detailedQuestionFilter === 'All' && <Check className="ml-2 h-4 w-4" />}
                </Button>
                <Button
                  className={
                    detailedQuestionFilter === 'Attempted Incorrect'
                      ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]/90 text-[var(--q3-neutral-default_inverted)] rounded-full'
                      : 'rounded-full'
                  }
                  variant={detailedQuestionFilter === 'Attempted Incorrect' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDetailedQuestionFilter('Attempted Incorrect')}
                >
                  Attempted Incorrect{' '}
                  {detailedQuestionFilter === 'Attempted Incorrect' && <Check className="ml-2 h-4 w-4" />}
                </Button>
                <Button
                  className={
                    detailedQuestionFilter === 'Not Attempted'
                      ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]/90 text-[var(--q3-neutral-default_inverted)] rounded-full'
                      : 'rounded-full'
                  }
                  variant={detailedQuestionFilter === 'Not Attempted' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDetailedQuestionFilter('Not Attempted')}
                >
                  Not Attempted {detailedQuestionFilter === 'Not Attempted' && <Check className="ml-2 h-4 w-4" />}
                </Button>
                <Button
                  className={
                    detailedQuestionFilter === 'Important'
                      ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]/90 text-[var(--q3-neutral-default_inverted)] rounded-full'
                      : 'rounded-full'
                  }
                  variant={detailedQuestionFilter === 'Important' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDetailedQuestionFilter('Important')}
                >
                  Important {detailedQuestionFilter === 'Important' && <Check className="ml-2 h-4 w-4" />}
                </Button>
                <div className="h-8 w-[1px] bg-[var(--q3-stroke-normal)]" />
                <Button
                  className={
                    communitySolutionFilter
                      ? 'bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)]/90 text-[var(--q3-neutral-default_inverted)] rounded-full'
                      : 'rounded-full'
                  }
                  variant={communitySolutionFilter ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCommunitySolutionFilter((prev) => !prev)}
                >
                  My Added Solutions {communitySolutionFilter && <Check className="ml-2 h-4 w-4" />}
                </Button>
              </div>
            )}

            {isLoadingContent ? (
              <ContentAreaSkeleton isTestWise={true} />
            ) : (
              <>
                {selectedMistakeReason === 'All' && (
                  <AttemptPatternPieChartCard
                    data={attemptOverviewPieData}
                    expanded={attemptingPatternExpanded}
                    onToggleExpand={() => setAttemptingPatternExpanded(!attemptingPatternExpanded)}
                  />
                )}

                <Card className="py-3 gap-0 mb-20 md:mb-0">
                  <CardHeader>
                    <CardTitle className="text-lg py-3 text-[var(--q3-neutral-default)]">All Mistaken Qs</CardTitle>
                  </CardHeader>
                  <hr />
                  <CardContent className="px-0">
                    <div className="overflow-x-auto">
                      <TabsContainer color={selectedSubject.color}>
                        {allTabs.map((tab) => (
                          <Tab
                            key={tab._id}
                            isActive={selectedSubject._id === tab._id}
                            color={
                              selectedSubject._id === tab._id
                                ? selectedSubject.title === 'All Qs'
                                  ? 'var(--q3-accent-normal)'
                                  : selectedSubject.color
                                : 'var(--q3-neutral-light)'
                            }
                            tabColor={tab.title === 'All Qs' ? 'var(--q3-accent-normal)' : tab.color}
                            onClick={() => handleSubjectChange(tab)}
                          >
                            {tab.title === 'All Qs' ? (
                              <img src={AllQs} alt={tab.title} className="h-auto w-6" />
                            ) : (
                              tab.icon && <img src={tab.icon} alt={tab.title} className="h-auto w-6" />
                            )}
                            <p className="mb-0">{tab.title}</p>
                          </Tab>
                        ))}
                      </TabsContainer>
                    </div>

                    <div className="space-y-0 mb-20">
                      {detailedQuestions.length > 0 ? (
                        detailedQuestions.map((question, index) => (
                          <QuestionListItem
                            key={question._id}
                            question={question}
                            index={index}
                            currentPage={detailedCurrentPage}
                            onClick={() => handleQuestionClick(index)}
                            displayTitle={formattedTestTitle}
                            packId={selectedPackId}
                            testId={testId}
                          />
                        ))
                      ) : (
                        <p className="text-center text-[var(--q3-neutral-light)] py-4">
                          {communitySolutionFilter
                            ? 'You haven’t added any Community Solutions yet. Start adding your own solutions!'
                            : 'No questions found for the selected filters.'}
                        </p>
                      )}
                    </div>

                    {detailedTotalPages > 1 && (
                      <Pagination className="mt-6">
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setDetailedCurrentPage((prev) => Math.max(1, prev - 1));
                              }}
                              className={detailedCurrentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                            />
                          </PaginationItem>
                          {paginationRange.map((pageNumber, index) => {
                            if (typeof pageNumber === 'string') {
                              return (
                                <PaginationItem key={`ellipsis-${index}`}>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              );
                            }
                            return (
                              <PaginationItem key={pageNumber}>
                                <PaginationLink
                                  href="#"
                                  isActive={pageNumber === detailedCurrentPage}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setDetailedCurrentPage(pageNumber);
                                  }}
                                >
                                  {pageNumber}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          })}
                          <PaginationItem>
                            <PaginationNext
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setDetailedCurrentPage((prev) => Math.min(detailedTotalPages, prev + 1));
                              }}
                              className={
                                detailedCurrentPage === detailedTotalPages ? 'pointer-events-none opacity-50' : ''
                              }
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
            <QuestionDrawer
              isOpen={isQuestionDrawerOpen}
              onClose={() => setIsQuestionDrawerOpen(false)}
              questions={detailedQuestions}
              currentQuestionIndex={currentQuestionIndex}
              onQuestionChange={handleQuestionChange}
              packId={selectedPackId}
              testId={testId}
            />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
